import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';
import type { Page } from '@/api/types';
import { usePagePrefs, useTableRegistry } from '@/hooks/usePagePrefs';
import {
  EXPORT_FORMAT_LABELS,
  exportTable,
  nodeToText,
  type ExportFormat,
} from '@/lib/export';
import { Button } from './Button';
import { EmptyState, ErrorState, TableSkeleton } from './Feedback';
import { Menu, MenuItem, MenuLabel } from './Overlay';

/**
 * One table for the whole application.
 *
 * Server-paged by design: a client that fetches every row to filter in the browser stops working
 * the day the client's data grows, and the backend already pages, filters and sorts properly.
 *
 * On narrow screens the table becomes a list of cards rather than a horizontally scrolling grid,
 * because a table scrolled sideways on a phone is unusable in practice.
 */

export interface Column<T> {
  id: string;
  header: ReactNode;
  /** Cell content. Receives the whole row so a cell can combine fields. */
  cell: (row: T) => ReactNode;
  /** Sort key understood by the backend. Omit for a non-sortable column. */
  sortKey?: string;
  className?: string;
  headerClassName?: string;
  /** Hidden below `lg`, where the card layout shows a curated subset instead. */
  hideOnMobile?: boolean;
  align?: 'left' | 'right';
  /**
   * The plain value for an export, when the rendered cell's own text is not it - an icon standing
   * in for a status, or a relative date where a spreadsheet wants the absolute one.
   */
  exportValue?: (row: T) => string;
  /**
   * Name for this column in the page settings menu. Defaults to the header when it is a plain
   * string; needed when the header is an element.
   */
  label?: string;
  /** Excludes the column from the settings menu, for one the screen cannot work without. */
  alwaysVisible?: boolean;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  page: Page<T> | undefined;
  isLoading: boolean;
  error?: unknown;
  onRetry?: () => void;
  getRowId: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Rendered when the query succeeded and returned nothing. */
  empty: { title: string; description?: string; action?: ReactNode; icon?: ReactNode };
  sort?: string;
  onSortChange?: (sort: string) => void;
  onPageChange?: (page: number) => void;
  /** Compact card headline for the mobile layout. */
  mobileTitle?: (row: T) => ReactNode;
  mobileMeta?: (row: T) => ReactNode;
  caption?: string;
  /** Turns the export menu off for a table whose rows are not a record anyone would take away. */
  exportable?: boolean;
  /**
   * Fetches one page of the same query the table is showing - same filters, same sort - so an
   * export can take every matching row rather than only the page on screen. Without it, export
   * falls back to the rows currently loaded.
   */
  fetchPage?: (page: number, size: number) => Promise<Page<T>>;
  /** Extra controls on the table's toolbar row, left of the export menu. */
  toolbar?: ReactNode;
}

export function DataTable<T>({
  columns: allColumns,
  page,
  isLoading,
  error,
  onRetry,
  getRowId,
  onRowClick,
  empty,
  sort,
  onSortChange,
  onPageChange,
  mobileTitle,
  mobileMeta,
  caption,
  exportable = true,
  toolbar,
  fetchPage,
}: DataTableProps<T>) {
  const { prefs } = usePagePrefs();
  const registry = useTableRegistry();
  const rows = page?.content ?? [];
  const compact = prefs.density === 'compact';

  /*
   * Publish this table's columns so the page settings menu can offer them.
   *
   * The registration is cleared on unmount, otherwise navigating from a list to a detail screen
   * leaves the previous page's column toggles in the header of a page that has no table.
   */
  const registerColumns = registry?.register;
  const columnSignature = allColumns.map((column) => column.id).join('|');
  useEffect(() => {
    if (!registerColumns) return;
    registerColumns({
      columns: allColumns
        .filter((column) => !column.alwaysVisible)
        .map((column) => ({
          id: column.id,
          label: column.label ?? (typeof column.header === 'string' ? column.header : column.id),
        })),
    });
    return () => registerColumns(null);
    // Column definitions are rebuilt on every render (they close over row data), so the identity
    // of the array is not a usable dependency - the set of ids is.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerColumns, columnSignature]);

  const columns = useMemo(
    () =>
      allColumns.filter(
        (column) => column.alwaysVisible || !prefs.hiddenColumns.includes(column.id),
      ),
    [allColumns, prefs.hiddenColumns],
  );

  const tableToolbar =
    toolbar || (exportable && rows.length > 0) ? (
      <div className="flex flex-wrap items-center justify-end gap-2">
        {toolbar}
        {exportable && rows.length > 0 && (
          <ExportMenu
            columns={columns}
            rows={rows}
            total={fetchPage ? (page?.totalElements ?? rows.length) : rows.length}
            fetchPage={fetchPage}
            title={caption ?? 'Export'}
          />
        )}
      </div>
    ) : null;

  /** Wraps whatever state the table is in, so the toolbar sits above all of them. */
  const frame = (children: ReactNode) => (
    <div className="space-y-2">
      {tableToolbar}
      {children}
    </div>
  );

  if (error) {
    return frame(
      <div className="rounded-xl border border-border-subtle bg-surface">
        <ErrorState error={error} onRetry={onRetry} className="border-0" />
      </div>,
    );
  }

  if (isLoading) {
    return frame(
      <div className="overflow-hidden rounded-xl border border-border-subtle bg-surface">
        <TableSkeleton columns={Math.min(columns.length, 6)} />
      </div>,
    );
  }

  if (rows.length === 0) {
    return frame(
      <div className="rounded-xl border border-border-subtle bg-surface">
        <EmptyState
          icon={empty.icon}
          title={empty.title}
          description={empty.description}
          action={empty.action}
        />
      </div>,
    );
  }

  const [currentSortKey, currentSortDirection] = (sort ?? '').split(',');

  const toggleSort = (sortKey: string) => {
    if (!onSortChange) return;
    const nextDirection =
      currentSortKey === sortKey && currentSortDirection === 'asc' ? 'desc' : 'asc';
    onSortChange(`${sortKey},${nextDirection}`);
  };

  return frame(
    <div className="overflow-hidden rounded-xl border border-border-subtle bg-surface">
      {/* Desktop: a real table, which is what a screen reader needs for row/column context. */}
      {/* Scrolls sideways inside its own box when the columns outgrow it: the frame clips its
          overflow for the rounded corners, so without this the last columns were silently cut off. */}
      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full border-collapse text-left">
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr className="border-b border-border-subtle bg-surface-sunken">
              {columns.map((column) => {
                const isSorted = column.sortKey && currentSortKey === column.sortKey;
                return (
                  <th
                    key={column.id}
                    scope="col"
                    aria-sort={
                      isSorted
                        ? currentSortDirection === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : column.sortKey
                          ? 'none'
                          : undefined
                    }
                    className={cn(
                      'text-label text-text-muted',
                      compact ? 'px-3 py-1.5' : 'px-4 py-2.5',
                      column.align === 'right' && 'text-right',
                      column.headerClassName,
                    )}
                  >
                    {column.sortKey && onSortChange ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(column.sortKey!)}
                        className="inline-flex items-center gap-1 rounded transition-colors hover:text-text-primary"
                      >
                        {column.header}
                        <span aria-hidden className="text-[0.625rem]">
                          {isSorted ? (currentSortDirection === 'asc' ? '▲' : '▼') : '⇅'}
                        </span>
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {rows.map((row) => (
              <tr
                key={getRowId(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  'transition-colors',
                  onRowClick && 'cursor-pointer hover:bg-surface-hover',
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.id}
                    className={cn(
                      'text-body text-text-secondary align-middle',
                      compact ? 'px-3 py-1.5' : 'px-4 py-3',
                      column.align === 'right' && 'text-right',
                      column.className,
                    )}
                  >
                    {column.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: cards. Each row becomes a tappable summary rather than a squeezed grid. */}
      <ul className="divide-y divide-border-subtle lg:hidden">
        {rows.map((row) => (
          <li key={getRowId(row)}>
            <div
              role={onRowClick ? 'button' : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={
                onRowClick
                  ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onRowClick(row);
                      }
                    }
                  : undefined
              }
              className={cn(
                compact ? 'px-4 py-2.5' : 'px-4 py-3.5',
                onRowClick && 'cursor-pointer hover:bg-surface-hover',
              )}
            >
              {mobileTitle ? (
                <div className="space-y-2">
                  <div className="text-h3 text-text-primary">{mobileTitle(row)}</div>
                  {mobileMeta && <div>{mobileMeta(row)}</div>}
                </div>
              ) : (
                <dl className="space-y-1.5">
                  {columns
                    .filter((column) => !column.hideOnMobile)
                    .map((column) => (
                      <div key={column.id} className="flex justify-between gap-3">
                        <dt className="text-caption text-text-muted">{column.header}</dt>
                        <dd className="min-w-0 text-right text-small text-text-primary">
                          {column.cell(row)}
                        </dd>
                      </div>
                    ))}
                </dl>
              )}
            </div>
          </li>
        ))}
      </ul>

      {page && onPageChange && page.totalPages > 1 && (
        <Pagination page={page} onPageChange={onPageChange} />
      )}
    </div>,
  );
}

export function Pagination<T>({
  page,
  onPageChange,
}: {
  page: Page<T>;
  onPageChange: (page: number) => void;
}) {
  const from = page.page * page.size + 1;
  const to = Math.min((page.page + 1) * page.size, page.totalElements);

  return (
    <nav
      aria-label="Pagination"
      className="flex flex-wrap items-center justify-between gap-3 border-t border-border-subtle bg-surface-sunken px-4 py-3"
    >
      <p className="text-caption text-text-muted tabular">
        <span className="font-medium text-text-secondary">
          {from.toLocaleString()}–{to.toLocaleString()}
        </span>{' '}
        of {page.totalElements.toLocaleString()}
      </p>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={page.first}
          onClick={() => onPageChange(page.page - 1)}
          leadingIcon={<ChevronLeft className="size-4" />}
        >
          Previous
        </Button>
        <span className="text-caption text-text-muted tabular">
          Page {page.page + 1} of {page.totalPages}
        </span>
        <Button
          size="sm"
          variant="secondary"
          disabled={page.last}
          onClick={() => onPageChange(page.page + 1)}
          trailingIcon={<ChevronRight className="size-4" />}
        >
          Next
        </Button>
      </div>
    </nav>
  );
}

/**
 * Export, in the formats people actually ask for.
 *
 * It exports the columns currently visible, in their current order, for the rows currently loaded
 * - so a reader who has filtered, sorted and hidden two columns gets exactly the table in front of
 * them. Values come from each column's `exportValue` when it has one and from the rendered cell's
 * text otherwise, which is what lets every existing table export without being touched.
 */
function ExportMenu<T>({
  columns,
  rows,
  total,
  fetchPage,
  title,
}: {
  columns: Column<T>[];
  rows: T[];
  total: number;
  fetchPage?: (page: number, size: number) => Promise<Page<T>>;
  title: string;
}) {
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const run = async (format: ExportFormat, close: () => void) => {
    close();
    setBusy(format);
    try {
      const all = fetchPage ? await fetchAllRows(fetchPage) : { rows, truncated: false };
      if (all.truncated) {
        toast.warning(`Exporting the first ${MAX_EXPORT_ROWS.toLocaleString()} rows`, {
          description: 'Narrow the filters to export the rest.',
        });
      }
      await exportTable(format, {
        filename: title,
        title,
        columns: columns.map((column) => ({
          id: column.id,
          header: column.label ?? (typeof column.header === 'string' ? column.header : column.id),
          value: (row: T) =>
            column.exportValue
              ? column.exportValue(row)
              : nodeToText(column.cell(row)).replace(/\s+/g, ' ').trim(),
        })),
        rows: all.rows,
      });
      toast.success(
        `Exported ${all.rows.length.toLocaleString()} ${all.rows.length === 1 ? 'row' : 'rows'}`,
      );
    } catch (cause) {
      toast.error(cause instanceof Error ? cause.message : 'The export failed.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Menu
      label="Export this table"
      trigger={
        <>
          {busy ? <Loader2 className="animate-spin" /> : <Download />}
          Export
        </>
      }
    >
      {(close) => (
        <>
          <MenuLabel>
            Export {fetchPage ? 'all ' : ''}
            {total.toLocaleString()} {total === 1 ? 'row' : 'rows'}
          </MenuLabel>
          {(['xlsx', 'csv', 'pdf'] as const).map((format) => (
            <MenuItem key={format} onClick={() => void run(format, close)} disabled={busy !== null}>
              {EXPORT_FORMAT_LABELS[format]}
            </MenuItem>
          ))}
        </>
      )}
    </Menu>
  );
}

/**
 * The ceiling on one export.
 *
 * Every row is fetched into the browser before the file is built, so this is what keeps a careless
 * click on an unfiltered register from pulling a hundred thousand records into a tab. Past it, the
 * user is told and asked to filter.
 * ponytail: browser-side, move to a streaming server export if anyone needs more than this.
 */
export const MAX_EXPORT_ROWS = 10_000;

/** The server caps page size at 200; asking for exactly that means the fewest round trips. */
const EXPORT_PAGE_SIZE = 200;

/**
 * Every row of the table's query, page by page.
 *
 * Sequential rather than parallel: an export is one person waiting on a download, and firing fifty
 * page requests at once would be the heaviest thing the server sees all day for no gain they notice.
 */
export async function fetchAllRows<T>(
  fetchPage: (page: number, size: number) => Promise<Page<T>>,
): Promise<{ rows: T[]; truncated: boolean }> {
  const first = await fetchPage(0, EXPORT_PAGE_SIZE);
  const collected = [...first.content];

  for (let index = 1; index < first.totalPages && collected.length < MAX_EXPORT_ROWS; index++) {
    const next = await fetchPage(index, EXPORT_PAGE_SIZE);
    collected.push(...next.content);
  }

  return {
    rows: collected.slice(0, MAX_EXPORT_ROWS),
    truncated: first.totalElements > MAX_EXPORT_ROWS,
  };
}
